import React, { useState } from "react"
import PropTypes from "prop-types"
import { DateTime } from "luxon"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"

import { useDebouncedCallback } from "use-debounce"
import { concretiseDate } from "../filters/filters"
import Select from "../../ui/Select"
import styled from "styled-components"

const Container = styled.div`
  width: 100%;
  select {
    width: 100%;
  }
`

/** Allows the user to select a time range. This is a single control that
 * effectively sets two filter values (from, to), either to a predefined,
 * dynamic time range as per `dateTimeProducer` in `filter.js`, or
 * a `custom` range where the user picks a start and end date/time.
 */
const PassesDateTimeFilter = ({ filter, setFilter, active }) => {
  // Map `from` and `to` to a single value for select
  const from = filter.from
  const to = filter.to
  // Map from filter to classifier
  const classifier =
    !from && !to
      ? ""
      : from == "now" && to == "p24h"
      ? "next24h"
      : from == "now" && to == "p48h"
      ? "next48h"
      : from == "now" && to == "p7d"
      ? "next7d"
      : "custom"

  // Classifier selected, map to filter
  const classifierChanged = (e) => {
    const c = e.target.value
    if (c == "") {
      setFilter({ ...filter, from: null, to: null })
    } else if (c == "next24h") {
      setFilter({ ...filter, from: "now", to: "p24h" })
    } else if (c == "next48h") {
      setFilter({ ...filter, from: "now", to: "p48h" })
    } else if (c == "next7d") {
      setFilter({ ...filter, from: "now", to: "p7d" })
    } else if (c == "custom") {
      // Set initial date range - 1 week?
      const now = DateTime.local()
      // If we already have dates, we use those so user can just tweak
      // To do that, we have to first make them concrete, i.e. filter could say `sd` (start of day)
      const from = filter.from
        ? concretiseDate(filter.from)
        : now.minus({ days: 7 }).startOf("day").toISO()
      const to = filter.to
        ? concretiseDate(filter.to)
        : now.endOf("day").toISO()
      setFilter({ ...filter, from, to })
    }
  }

  // Track changes to custom date/time in local state - pre-populating
  // from URL parameters if we're in 'custom' mode already
  const [customFrom, setCustomFrom] = useState(null) // Date obj
  const [customTo, setCustomTo] = useState(null) // Date obj

  // Update parent filter from custom from/to in state
  const updateParentUsingCustomDebounced = useDebouncedCallback(() => {
    setFilter({
      ...filter,
      from: customFrom.toISOString(),
      to: customTo.toISOString(),
    })
  }, 1000)

  // Custom date changed, update filter
  const handleCustomChanged = (fromTo) => (date) => {
    if (fromTo === "from") {
      setCustomFrom(date)
      if (customTo == null) {
        setCustomTo(new Date(filter.to))
      }
    } else if (fromTo === "to") {
      setCustomTo(date)
      if (customFrom == null) {
        setCustomFrom(new Date(filter.from))
      }
    }
    updateParentUsingCustomDebounced()
  }

  return (
    <Container>
      <Select value={classifier} onChange={classifierChanged} active={active}>
        <option value="next24h">Next 24 hours</option>
        <option value="next48h">Next 48 hours</option>
        <option value="next7d">Next 7 days</option>
        <option value="custom">Custom dates</option>
      </Select>
      {classifier == "custom" && (
        <div>
          <div>
            <label>Fr</label>
            <DatePicker
              dateFormat="y-MM-dd HH:mm:ss"
              showTimeSelect
              selected={customFrom || new Date(concretiseDate(filter.from))}
              onChange={handleCustomChanged("from")}
            />
          </div>
          <div>
            <label>To</label>
            <DatePicker
              dateFormat="y-MM-dd HH:mm:ss"
              showTimeSelect
              selected={customTo || new Date(concretiseDate(filter.to))}
              onChange={handleCustomChanged("to")}
            />
          </div>
        </div>
      )}
    </Container>
  )
}

PassesDateTimeFilter.propTypes = {
  filter: PropTypes.object.isRequired,
  setFilter: PropTypes.func.isRequired,
  active: PropTypes.bool,
}

export default PassesDateTimeFilter
